/*
 * manmade-article-prices.js
 * ------------------------------------------------------------------
 * Keeps the price / compare-at / "$X off" figures inside blog-post CTAs
 * in sync with the LIVE product offer, so posts never have to be edited
 * when a price or Kaching deal changes.
 *
 * Blog posts are pasted HTML (article.content), so the numbers are plain
 * text. This script runs after the article renders and rewrites, in
 * place, only these three things:
 *
 *   1. Savings copy  — "$80 Off", "Claim $80 OFF →", "$30 Discount"
 *                      → the live (compare-at − price), rounded to the
 *                      nearest $5 step like every other savings badge.
 *   2. Struck prices — <s>$180.00</s> / <del>…</del> → live compare-at.
 *   3. Price boxes   — elements whose class contains "price" that sit in
 *                      a card mentioning / linking the product
 *                      → live selling price.
 *
 * Everything else (prose, competitor prices, "$12,000 cast saw") is left
 * exactly as written.
 *
 * Which product? Each figure is matched to the nearest product link
 * (/products/<handle>) around it; when none is nearby, the post's most
 * linked product wins. Handles that 404 are skipped (text untouched).
 *
 * Where do the numbers come from? (nothing hard-coded)
 *   • /products/<handle>.js  → base price + native compare-at.
 *   • Kaching deal_blocks (Storefront API, same cache as
 *     manmade-bundle-price.js) → the deal price the PDP will show. The
 *     Kaching A/B bucket is reproduced from the visitor's own
 *     kaching_session_id, so the blog shows the SAME variant price the
 *     reader gets on the PDP.
 *
 * Fully defensive: any failure leaves the article untouched.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CACHE_KEY    = 'mm_kb_deals_v1';
  var CACHE_TTL_MS = 30 * 60 * 1000;
  var API_VERSION  = '2026-01';
  var FALLBACK_DOMAIN = 'vj76m0-i3.myshopify.com';
  var FALLBACK_TOKEN  = '34e8f863caf7638d143c5724570af75e';
  var KB_SESSION_KEY  = 'kaching_session_id';

  var SAVINGS_RE = /\$\s?(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?)(\s*)(off|discount)\b/gi;
  var MONEY_RE   = /\$\s?(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?)/g;

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else { fn(); }
  }

  // ---- Kaching config / deals (mirrors manmade-bundle-price.js) --------
  function readKachingConfig() {
    var cfg = { domain: FALLBACK_DOMAIN, token: FALLBACK_TOKEN };
    try {
      var el = document.getElementById('kaching-bundles-config');
      if (el && el.textContent) {
        var j = JSON.parse(el.textContent);
        if (j.shopifyDomain) cfg.domain = j.shopifyDomain;
        if (j.storefrontAccessToken) cfg.token = j.storefrontAccessToken;
      }
    } catch (e) { /* keep fallbacks */ }
    return cfg;
  }

  function getDeals(cfg) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && (Date.now() - cached.ts) < CACHE_TTL_MS && Array.isArray(cached.deals)) {
          return Promise.resolve(cached.deals);
        }
      }
    } catch (e) { /* ignore */ }
    var url = 'https://' + cfg.domain + '/api/' + API_VERSION + '/graphql.json';
    var query = '{shop{metafield(namespace:"$app:kaching_bundles",key:"deal_blocks"){value}}}';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.token },
      body: JSON.stringify({ query: query })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var value = data && data.data && data.data.shop && data.data.shop.metafield && data.data.shop.metafield.value;
        var deals = value ? JSON.parse(value) : [];
        if (!Array.isArray(deals)) deals = [];
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), deals: deals })); } catch (e) {}
        return deals;
      })
      .catch(function () { return []; });
  }

  // ---- Kaching A/B bucket (same maths as the widget) --------------------
  function kachingStorage() {
    try {
      var t = 'kaching_local_storage_test';
      localStorage.setItem(t, t); localStorage.removeItem(t);
      return window.localStorage;
    } catch (e) { return window.sessionStorage; }
  }
  function uuid() {
    if (window.crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, function (c) {
      return (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16);
    });
  }
  function kachingSessionId() {
    try {
      var st = kachingStorage();
      var id = st.getItem(KB_SESSION_KEY);
      if (!id) { id = uuid(); st.setItem(KB_SESSION_KEY, id); } // widget reuses it
      return id;
    } catch (e) { return null; }
  }
  function bucketOf(sessionId) {
    var n = parseInt(String(sessionId).replace(/-/g, '').slice(0, 4), 16) % 256;
    return Math.floor(n * 100 / 256);
  }
  function variantApplies(deal, bucket) {
    if (!deal.abTestVariantId) return true;
    if (bucket == null) return false;
    var r = deal.abTestVariantRange;
    if (r && r.length === 2) return bucket >= r[0] && bucket < r[1];
    var num = deal.abTestVariantNumber, cnt = deal.abTestVariantsCount, alloc = deal.abTestTrafficAllocation;
    if (!num || !cnt) return false;
    var pick;
    if (!alloc) pick = Math.min(Math.floor(bucket / (100 / cnt)) + 1, cnt);
    else {
      var rest = 100 - alloc;
      if (bucket < rest) pick = 1;
      else {
        var others = cnt - 1;
        pick = others === 0 ? 1 : Math.min(Math.floor((bucket - rest) / (alloc / others)) + 2, cnt);
      }
    }
    return num === pick;
  }

  function effectiveCents(baseCents, type, value) {
    var dv = Number(value) || 0;
    if (type === 'percentage') return Math.round(baseCents * (1 - dv / 100));
    if (type === 'amount')     return Math.max(0, baseCents - Math.round(dv * 100));
    if (type === 'specific')   return Math.round(dv * 100);
    return baseCents;
  }

  // The single-unit deal price the PDP will show this visitor, or null.
  function dealPriceFor(deals, pid, baseCents, bucket) {
    for (var i = 0; i < deals.length; i++) {
      var d = deals[i];
      if (!d || !Array.isArray(d.dealBars) || d.marketId) continue;
      var sel = (d.selectedProductIds || []).map(String);
      if (sel.indexOf(String(pid)) === -1) continue;
      if ((d.excludedProductIds || []).map(String).indexOf(String(pid)) !== -1) continue;
      if (!variantApplies(d, bucket)) continue;
      var bars = d.dealBars.filter(function (b) {
        return b && b.dealBarType === 'quantity-break' && (Number(b.quantity) || 1) === 1 && !b.sellingPlanEnabled;
      });
      if (!bars.length) return null;
      var bar = null;
      for (var b = 0; b < bars.length; b++) if (bars[b].id === d.preselectedDealBarId) bar = bars[b];
      bar = bar || bars[0];
      return effectiveCents(baseCents, bar.discountType, bar.discountValue);
    }
    return null;
  }

  // ---- Product data -------------------------------------------------------
  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // ---- Money helpers --------------------------------------------------------
  // Savings copy: whole dollars, never pennies ("$120 off").
  function fmt(cents) {
    return '$' + String(Math.round(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  // Prices / compare-at: the theme's "Price display" setting (window.mmMoney),
  // so blog CTAs match the PDP and cards. Fallback = cents.
  function money(cents) {
    if (typeof window.mmMoney === 'function') return window.mmMoney(cents);
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  // Savings: nearest $5 step, no pennies; under $5 stays exact (site rule).
  function savingsCents(compare, price) {
    var diff = compare - price;
    if (diff <= 0) return 0;
    if (diff < 500) return diff;
    return Math.round(diff / 500) * 500;
  }

  // ---- DOM helpers ----------------------------------------------------------
  function handleFromHref(href) {
    var m = String(href || '').match(/\/products\/([a-z0-9-]+)/i);
    return m ? m[1].toLowerCase() : null;
  }
  function handlesIn(el) {
    var counts = {};
    var links = el.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var h = handleFromHref(links[i].getAttribute('href'));
      if (h) counts[h] = (counts[h] || 0) + 1;
    }
    if (el.tagName === 'A') { var own = handleFromHref(el.getAttribute('href')); if (own) counts[own] = (counts[own] || 0) + 1; }
    return counts;
  }
  function topHandle(counts) {
    var best = null, n = 0;
    for (var k in counts) if (counts[k] > n) { best = k; n = counts[k]; }
    return best;
  }
  // Nearest product around an element: walk up until some ancestor contains a product link.
  function productFor(el, root, offers, fallback) {
    var node = el;
    while (node && node !== root) {
      var h = topHandle(handlesIn(node));
      if (h) return offers[h] || null;
      node = node.parentElement;
    }
    return fallback;
  }
  function isStruck(node) {
    var el = node.nodeType === 3 ? node.parentElement : node;
    while (el) {
      var tag = el.tagName;
      if (tag === 'S' || tag === 'DEL' || tag === 'STRIKE') return true;
      var cls = String(el.className || '');
      if (/compare-?_?at|strike|full-?_?price|was-?_?price|old-?_?price/i.test(cls)) return true;
      if (/line-through/i.test(el.getAttribute('style') || '')) return true;
      if (el.hasAttribute('data-mm-article-content')) break;
      el = el.parentElement;
    }
    return false;
  }
  // The surrounding card must link or name the product, and `el` must be
  // the only price element inside that card. A comparison table whose
  // wrapper names our product but also holds a competitor's price fails
  // here, so both figures are left alone.
  function inProductCard(el, root, offer) {
    var card = el, hops = 0;
    while (card && card !== root && hops < 5) {
      if (Object.keys(handlesIn(card)).length || offer.titleRe.test(card.textContent || '')) {
        var leaves = card.querySelectorAll('[class*="price"]'), others = 0;
        for (var i = 0; i < leaves.length; i++) {
          var lf = leaves[i];
          if (lf === el || lf.contains(el) || el.contains(lf)) continue;
          if (lf.querySelector('[class*="price"]')) continue;          // wrapper, not a leaf
          if ((lf.textContent || '').indexOf('$') !== -1) others++;
        }
        return others === 0;
      }
      card = card.parentElement; hops++;
    }
    return false;
  }
  function textNodesIn(el) {
    var out = [], w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var n; while ((n = w.nextNode())) { if (n.nodeValue && n.nodeValue.indexOf('$') !== -1) out.push(n); }
    return out;
  }

  // ---- Rewrite ------------------------------------------------------------------
  function apply(root, offers, fallback) {
    var changed = 0;

    // 1. Savings copy anywhere: "$80 Off" / "$80 OFF →" / "$30 Discount".
    textNodesIn(root).forEach(function (node) {
      var text = node.nodeValue;
      if (!SAVINGS_RE.test(text)) { SAVINGS_RE.lastIndex = 0; return; }
      SAVINGS_RE.lastIndex = 0;
      var offer = productFor(node.parentElement, root, offers, fallback);
      if (!offer || !offer.savings) return;
      var next = text.replace(SAVINGS_RE, function (all, amt, sp, word) {
        return fmt(offer.savings) + sp + word;
      });
      if (next !== text) { node.nodeValue = next; changed++; }
    });

    // 2. Struck compare-at prices (<s>, <del>, .compare…).
    // Explicit strike semantics only: a "compare"/"was" class alone is too
    // common (e.g. a competitor comparison table) to trust.
    var struckEls = root.querySelectorAll('s, del, strike, [class*="compare-at"], [class*="compare_at"], [class*="compareat"], [class*="strike"], [class*="full-price"], [class*="fullprice"], [class*="was-price"], [class*="old-price"]');
    for (var i = 0; i < struckEls.length; i++) {
      var sEl = struckEls[i];
      if (sEl.closest('a')) continue;
      var sNodes = textNodesIn(sEl);
      if (!sNodes.length) continue;
      var sOffer = productFor(sEl, root, offers, fallback);
      if (!sOffer || !sOffer.compare) continue;
      if (!inProductCard(sEl, root, sOffer)) continue;
      sNodes.forEach(function (node) {
        var t = node.nodeValue, cnt = 0;
        var next = t.replace(MONEY_RE, function (all, amt) {
          if (/\boff\b|discount/i.test(t)) return all;    // savings, handled above
          cnt++; return money(sOffer.compare);
        });
        if (cnt === 1 && next !== t) { node.nodeValue = next; changed++; }
      });
    }

    // 3. Selling price inside "price" elements that belong to a product card.
    var priceEls = root.querySelectorAll('[class*="price"]');
    for (var p = 0; p < priceEls.length; p++) {
      var pEl = priceEls[p];
      if (pEl.closest('a')) continue;
      if (pEl.querySelector('[class*="price"]')) continue;       // handle leaf elements only
      var pOffer = productFor(pEl, root, offers, fallback);
      if (!pOffer) continue;
      if (!inProductCard(pEl, root, pOffer)) continue;
      var nodes = textNodesIn(pEl);
      nodes.forEach(function (node) {
        if (isStruck(node)) return;
        var t = node.nodeValue;
        if (/\boff\b|discount/i.test(t)) return;
        var matches = t.match(MONEY_RE) || [];
        if (matches.length !== 1) return;                      // only unambiguous single prices
        var next = t.replace(MONEY_RE, function () { return money(pOffer.price); });
        if (next !== t) { node.nodeValue = next; changed++; }
      });
    }
    return changed;
  }

  // ---- Theme-built live offer hooks (sections/listicle-trimmer.liquid) -------------
  //   [data-mm-live-offer="<handle>"] → fills [data-mm-live-now] (price),
  //       [data-mm-live-was] (compare-at, parent <s> hidden when none) and
  //       [data-mm-live-save] ("Save $X", hidden when none).
  //   [data-mm-live-cta="<handle>"] + data-mm-cta-tpl="CLAIM [x] OFF" → label
  //       with [x] = rounded savings; left as rendered when there are none.
  // Every hook gets data-mm-live-ready once handled (CSS holds them hidden until then).
  function fillLive(offers) {
    var changed = 0;
    var boxes = document.querySelectorAll('[data-mm-live-offer]');
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i], o = offers[box.getAttribute('data-mm-live-offer')];
      if (o) {
        var now = box.querySelector('[data-mm-live-now]');
        var was = box.querySelector('[data-mm-live-was]');
        var save = box.querySelector('[data-mm-live-save]');
        if (now) now.textContent = money(o.price);
        if (was) {
          var wrap = was.closest('s, del') || was;
          if (o.compare) { was.textContent = money(o.compare); wrap.hidden = false; } else { wrap.hidden = true; }
        }
        if (save) {
          if (o.savings) { save.textContent = 'Save ' + fmt(o.savings); save.hidden = false; } else { save.hidden = true; }
        }
        changed++;
      }
      box.setAttribute('data-mm-live-ready', '');
    }
    var ctas = document.querySelectorAll('[data-mm-live-cta]');
    for (var c = 0; c < ctas.length; c++) {
      var cta = ctas[c], co = offers[cta.getAttribute('data-mm-live-cta')];
      var tpl = cta.getAttribute('data-mm-cta-tpl');
      if (co && co.savings && tpl) {
        var txt = cta.querySelector('[data-mm-cta-text]') || cta;
        txt.textContent = tpl.replace('[x]', fmt(co.savings));
        changed++;
      }
      cta.setAttribute('data-mm-live-ready', '');
    }
    return changed;
  }
  function releaseLive() {
    var els = document.querySelectorAll('[data-mm-live-offer], [data-mm-live-cta]');
    for (var i = 0; i < els.length; i++) els[i].setAttribute('data-mm-live-ready', '');
  }

  // ---- Main -----------------------------------------------------------------------
  onReady(function () {
    if (window.__mmArticlePricesWired) return;
    window.__mmArticlePricesWired = true;
    var root = document.querySelector('[data-mm-article-content]');
    var live = document.querySelectorAll('[data-mm-live-offer], [data-mm-live-cta]');
    if (!root && !live.length) return;
    var counts = root ? handlesIn(root) : {};
    var primary = topHandle(counts);
    for (var l = 0; l < live.length; l++) {
      var lh = live[l].getAttribute('data-mm-live-offer') || live[l].getAttribute('data-mm-live-cta');
      if (lh && !(lh in counts)) counts[lh] = 0;
    }
    var handles = Object.keys(counts);
    if (!handles.length) { releaseLive(); return; }

    var cfg = readKachingConfig();
    var bucket = null;
    try { var sid = kachingSessionId(); if (sid) bucket = bucketOf(sid); } catch (e) {}

    Promise.all([getDeals(cfg)].concat(handles.map(fetchProduct))).then(function (res) {
      var deals = res[0] || [];
      var offers = {};
      for (var i = 0; i < handles.length; i++) {
        var prod = res[i + 1];
        if (!prod || !prod.id) continue;
        var base = Number(prod.price);
        var nativeCompare = prod.compare_at_price ? Number(prod.compare_at_price) : null;
        var deal = dealPriceFor(deals, prod.id, base, bucket);
        var price = (deal != null && deal > 0 && deal < base) ? deal : base;
        var compare = nativeCompare && nativeCompare > price ? nativeCompare : (price < base ? base : null);
        offers[handles[i]] = {
          handle: handles[i], id: prod.id, title: prod.title,
          titleRe: new RegExp(String(prod.title).replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+'), 'i'),
          price: price, compare: compare,
          savings: compare ? savingsCents(compare, price) : 0
        };
      }
      if (!Object.keys(offers).length) { releaseLive(); return; }
      var fallback = offers[primary] || offers[Object.keys(offers)[0]];
      var changed = root ? apply(root, offers, fallback) : 0;
      changed += fillLive(offers);
      try { window.__mmArticlePrices = { bucket: bucket, offers: offers, changed: changed }; } catch (e) {}
    }).catch(function () { releaseLive(); /* leave article untouched */ });
  });
})();
